import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of, from, never } from 'rxjs';
import { Location } from '@angular/common';
import { catchError, map, switchMap, tap, withLatestFrom, take, debounceTime, takeLast, throttleTime, debounce, delay, filter } from 'rxjs/operators';
import { BusinessService } from '@services/business/business.service'
import { BusinessActions, LocationsActions } from '@actions/index'
import { BusinessModel } from '@models/business.model';
import * as fromApp from '@reducers/index'
import { ResellerModel, LocationModel, RequestModel } from '@models/index';
import { Store } from '@ngrx/store';
import { HttpApiService } from '@services/http-api.service';
import { RouterFacadeService } from '@services/router-facade.service';
import { BusinessFacadeService } from '@services/business/business-facade.service';
import { emptyBusinessModel, emptyLocationModel } from '@utilities/data-utils';
import { MessageService } from 'primeng/api';
import { LocationsFacadeService } from '@services/locations/locations-facade.service';
import { LocationsService } from '@services/locations/locations.service';

@Injectable()
export class BusinessEffects {

    searchBusinesses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.searchBusinesses),
            switchMap(({ request }) =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as { data: BusinessModel[], total: number }),
                        map(data => BusinessActions.searchBusinessesSuccess({ ...data })),
                        catchError(error => of(BusinessActions.businessError({ error: error.error })))
                    )
            )
        )
    )

    searchArchivedBusinessesByQuery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.searchArchivedBusinessesByQuery),
            switchMap(({ request }) =>
                this.httpApiService
                    .getRequest(request).pipe(
                        map(data => data as { data: BusinessModel[], total: number }),
                        map(data => BusinessActions.searchBusinessesSuccess({ ...data })),
                        catchError(error => of(BusinessActions.businessError({ error })))
                    )
            )
        )
    )

    searchBusinessById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.searchBusinessById),
            switchMap(({ businessId }) =>
                this.httpApiService.getRequest({ params: `business/${businessId}` }).pipe(
                    map(data => data as BusinessModel),
                    filter(data => data ?  true : false),
                    map(business => BusinessActions.searchBusinessByIdSuccess({ business })),
                    catchError(error => of(BusinessActions.businessError({ error: error.error })))
                )
            )
        )
    )

    searchContactsAddedOnPeriodOfTime$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.searchContactsAddedOnPeriodOfTime),
            switchMap(({ request }) =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as [{count: number, locationId: string}]),
                        map(data => BusinessActions.searchContactsAddedOnPeriodOfTimeSuccess({ data })),
                        catchError(error => of(BusinessActions.businessError({ error: error.error })))
                    )
            )
        )
    )

    searchBusinessesByQuery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.searchBusinessesByQuery),
            switchMap(({ request }) =>
                this.httpApiService
                    .getRequest(request).pipe(
                        map(data => data as { data: BusinessModel[], total: number }),
                        map(data => BusinessActions.searchBusinessesSuccess({ ...data })),
                        catchError(error => of(BusinessActions.businessError({ error })))
                    )
            )
        )
    )

    createBusiness$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.createBusiness),
            map(action => action.business),
            withLatestFrom(
                this.store.select(fromApp.getSelectedResellerProfile),
                (business: BusinessModel, reseller: ResellerModel) => ({
                    ...business,
                    resellerId: reseller.id
                })
            ),
            throttleTime(4000),
            switchMap((business: BusinessModel) =>
                this.businessService.createBusiness(business)
                    .pipe(
                        tap(business => this.launchToastMssg('success', 'Create Business', `The business ${business.name} has been created`)),
                        map(business => BusinessActions.createBusinessSuccess({ business })),
                        tap(() => this.routerFacadeService.navigateTo$('admin-business/locations')),
                        catchError(error => of(BusinessActions.businessError({ error })))
                    )
            )
        )
    )

    createBusinessWithLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.createBusinessWithLocation),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedResellerProfile),
                (request: RequestModel, reseller: ResellerModel) => ({
                    ...request,
                    payload: { ...request.payload.business, resellerId: reseller.id },
                    location: request.payload.location
                })
            ),
            switchMap((request) => {
                const location = request.location
                return this.httpApiService.postRequest({ ...{}, params: request.params, payload: request.payload })
                    .pipe(
                        tap((business: BusinessModel) => {
                            this.launchToastMssg('success', 'Create Business', `The business ${business.name} has been created`)
                        }),
                        map((business: BusinessModel) => {
                            this.businessFacadeService.selectBusinessProfile(business)
                            this.businessFacadeService.createBusinessWithLocationSucces(location)
                            return BusinessActions.createBusinessSuccess({ business })
                        }),
                        tap(() => {
                          // temporary workaround to avoid breaking available locations; forces all locations, search bar, etc to reload
                          setTimeout(() => {
                            this.window.location = 'admin-business/locations';
                          }, 2500);
                        }),
                        catchError(error => of(BusinessActions.businessError({ error: error.message })))
                    )
            }
            )
        )
    )

    createBusinessWithLocationSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.createBusinessWithLocationSuccess),
            map(action => action.location),
            withLatestFrom(
                this.store.select(fromApp.getSelectedBusinessProfile),
                (location: LocationModel, business: BusinessModel) => ({
                    ...{},
                    location: { ...location, businessId: business.id },
                    business: business
                })
            ),
            switchMap((request) => {
                const business = request.business
                return this.locationsService
                    .addLocation(request.location)
                    .pipe(
                        tap(location => this.launchToastMssg('success', 'Create Location', `The location ${location.name} has been created`)),
                        map(location => {
                            this.businessFacadeService.editBusiness(Object.assign({}, business, { preferredLocation: location.id }))
                            return LocationsActions.addLocationSuccess({ location })
                        }),
                        tap(() => {
                            this.routerFacadeService.gotoBusinessDashboard$()
                        }),
                        catchError(error => of(LocationsActions.locationError({ error: error.message })))

                    )
            })
        )
    )

    editBusiness$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.editBusiness),
            map(action => action.businessEdited),
            switchMap((business: BusinessModel) => {
                return this.businessService.editBusiness(business)
                    .pipe(
                        tap(business => this.launchToastMssg('success', 'Edit Business', `The business ${business.name} has been edited`)),
                        map(business => BusinessActions.editBusinessSuccess({ business })),
                        tap(() => this.routerFacadeService.navigateTo$('admin-business/locations')),
                        catchError(error => of(BusinessActions.businessError({ error })))
                    )
            }
            )
        )
    )

    editBusinessPreferredLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.editBusinessPreferredLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ preferredLocation }, business]) => {
                return this.businessService.editBusiness({ ...business, preferredLocation })
                    .pipe(
                        // tap(business => this.launchToastMssg('success', 'Edit Business', `The business ${business.name} has been edited`)),
                        map(business => BusinessActions.editBusinessSuccess({ business })),
                        catchError(error => of(BusinessActions.businessError({ error })))
                    )
            })
        )
    )

    deleteBusiness$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.deleteBusiness),
            map(action => action.businessId),
            switchMap((businessId: string) =>
                this.businessService.deleteBusiness(businessId).pipe(
                    tap(_business => this.launchToastMssg('success', 'Business Deleted', `The business has been deleted.`)),
                    map(business => BusinessActions.deleteBusinessSuccess({ businessIdDeleted: business })),
                    tap(() => {
                        this.routerFacadeService.navigateTo$('reseller/business');
                        this.businessFacadeService.selectBusinessProfile(emptyBusinessModel());
                        this.locationFacadeService.setCurrentLocation(emptyLocationModel())
                    }),
                    catchError(error => {
                        this.launchToastMssg('error', 'Deletion Failed', `${(error.error && error.error.message) || error}`);
                        return of(BusinessActions.businessError({ error }));
                    }),
                )
            )
        )
    )

    archiveBusiness$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.archiveBusiness),
            map(action => action.businessId),
            switchMap((businessId: string) =>
                this.businessService.archiveBusiness(businessId).pipe(
                    tap(_business => this.launchToastMssg('success', 'Business Archived', `The business has been archived successfully.`)),
                    map(businessId => BusinessActions.archiveBusinessSuccess({ businessId })),
                    catchError(error => {
                        this.launchToastMssg('error', 'Archival Failed', `${error}`);
                        return of(BusinessActions.businessError({ error }))
                    })
                )
            )
        )
    )

    searchBusinessByUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.searchBusinessesByUser),
            switchMap(({ userId }) =>
                this.businessService
                    .searchBusinessesByUser(userId)
                    .pipe(
                        map(businesses => BusinessActions
                            .searchBusinessesByUserSuccess({ businesses })
                        ),
                        catchError(error => of(BusinessActions.businessError({ error })))
                    )
            )
        )
    )

    searchBusinessesByReseller$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.searchBusinessesByReseller),
            map(action => action.pages),
            switchMap((pages) =>
                this.businessService.searchBusinesByReseller(pages)
                    .pipe(
                        map(data => BusinessActions.searchBusinessesByResellerSuccess({ ...data })),
                        catchError(error => of(BusinessActions.businessError({ error: error.error })))
                    )
            )
        )
    )

    deArchiveBusiness$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.deArchiveBusiness),
            switchMap(({ request }) =>
                this.httpApiService
                    .postRequest(request).pipe(
                        map(data => data as BusinessModel),
                        map(data => BusinessActions.deArchiveBusinessSuccess({ business: data })),
                        catchError(error => of(BusinessActions.businessError({ error })))
                    )
            )
        )
    )

    searchBusinessesByQueryByReseller$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BusinessActions.searchBusinessesByQueryByReseller),
            map((action) => ({ page: action.page, size: action.size, query: action.query })),
            withLatestFrom(
                this.store.select(fromApp.getSelectedResellerProfile),
                (payload: { page: number, size: number, query: string }, reseller: ResellerModel) => ({
                    ...payload,
                    resellerId: reseller.id
                })
            ),
            switchMap((payload) =>
                this.businessService.searchBusinessesByQueryByReseller(payload).pipe(
                    map(data => BusinessActions.searchBusinessesSuccess({ ...data })),
                    catchError(error => of(BusinessActions.businessError({ error })))
                )
            )
        )
    )

    constructor(
        private actions$: Actions,
        private businessService: BusinessService,
        private httpApiService: HttpApiService,
        private routerFacadeService: RouterFacadeService,
        private businessFacadeService: BusinessFacadeService,
        private locationFacadeService: LocationsFacadeService,
        private messageToast: MessageService,
        private store: Store<any>,
        private location: Location,
        private locationsService: LocationsService,
        private window: Window,
    ) { }

    launchToastMssg(severity: string, summary: string, detail: string) {
        this.messageToast.add({ severity: severity, summary: summary, detail: detail, life: 5000 });
    }
}
